import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiClient } from "@/lib/api/client";
import { StateDto, CreateStateDto, UpdateStateDto, StateFilterParams } from "@/lib/types/api";
import { showToast } from "@/lib/utils/toast";
import { handleApiError } from "@/lib/utils/errors";
import { SEARCH_DEBOUNCE_MS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, Column } from "@/components/datatable/DataTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, MapPin, Search } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { LoadingButton } from "@/components/ui/loading";
import { StatusBadge } from "@/components/ui/status-badge";

const stateSchema = z.object({
  stateName: z.string().min(1, "State name is required"),
  lgdCode: z.string().optional(),
});

type StateFormData = z.infer<typeof stateSchema>;

export default function StatesPage() {
  const [states, setStates] = useState<StateDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Search and sorting states
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("stateName");
  const [sortDescending, setSortDescending] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<StateFormData>({
    resolver: zodResolver(stateSchema),
  });

  // Load states with current filters
  const loadStates = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const filterParams: StateFilterParams = {
        pageNumber: currentPage,
        pageSize: pageSize,
        searchTerm: searchQuery || undefined,
        sortBy: sortBy,
        sortDescending: sortDescending,
      };
      
      const response = await apiClient.getStatesPagedWithFilters(filterParams);
      
      if (response.success && response.data) {
        setStates(response.data.items || []);
        setTotalCount(response.data.totalCount || 0);
        setTotalPages(Math.ceil((response.data.totalCount || 0) / pageSize));
      }
    } catch (err: unknown) {
      showToast.error(handleApiError(err, "Failed to load states"));
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, sortBy, sortDescending]);
  
  // Single effect to handle all data loading with proper debouncing
  useEffect(() => {
    // For page/pageSize changes, load immediately without debounce
    if (searchQuery === "" && sortBy === "stateName" && !sortDescending) {
      loadStates();
      return;
    }

    // For search/sort changes, debounce
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadStates();
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [currentPage, pageSize, searchQuery, sortBy, sortDescending, loadStates]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(false);
    }
  };

  const onSubmit = async (data: StateFormData) => {
    try {
      setIsSubmitting(true);
      const lgdCodeValue = data.lgdCode && data.lgdCode.trim() !== '' ? Number(data.lgdCode) : null;
      
      if (editingId) {
        const updateData: UpdateStateDto = {
          stateName: data.stateName,
          lgdCode: lgdCodeValue,
          isActive: true,
        };
        await apiClient.updateState(editingId, updateData);
        showToast.success("State updated successfully");
      } else {  
        const createData: CreateStateDto = {
          stateName: data.stateName,
          lgdCode: lgdCodeValue,
        };
        await apiClient.createState(createData);
        showToast.success("State created successfully");
      }
      setShowForm(false);
      setEditingId(null);
      reset();
      loadStates();
    } catch (err: unknown) {
      showToast.error(handleApiError(err, editingId ? "Failed to update state" : "Failed to create state"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (state: StateDto) => {
    setEditingId(state.id);
    reset({
      stateName: state.stateName,
      lgdCode: state.lgdCode !== null && state.lgdCode !== undefined ? state.lgdCode.toString() : undefined,
    });
    setShowForm(true);
  };

  const handleDeleteRequest = (id: string) => {
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await apiClient.deleteState(deleteId);
      showToast.success("State deleted successfully");
      setDeleteId(null);
      loadStates();
    } catch (err: unknown) {
      showToast.error(handleApiError(err, "Failed to delete state"));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    reset();
  };

  const handleOpenNew = () => {
    setEditingId(null);
    reset({
      stateName: "",
      lgdCode: undefined,
    });
    setShowForm(true);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const columns: Column<StateDto>[] = [
    {
      key: "stateName",
      header: "Name",
      sortable: true,
      render: (s) => <span className="font-medium text-gray-900 dark:text-gray-100">{s.stateName}</span>,
    },
    {
      key: "lgdCode",
      header: "LGD Code",
      sortable: true,
      render: (s) => <span className="text-sm text-gray-600 dark:text-gray-400">{s.lgdCode ?? <span className="text-gray-400">—</span>}</span>,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (s) => <StatusBadge isActive={s.isActive} />,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      render: (s) => <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(s.createdAt)}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Create / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent onClose={handleCancel}>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit State" : "New State"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="State Name"
              placeholder="e.g., Maharashtra"
              error={errors.stateName?.message}
              {...register("stateName")}
            />

            <Input
              label="LGD Code (Optional)"
              type="number"
              placeholder="e.g., 27"
              error={errors.lgdCode?.message}
              {...register("lgdCode")}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
                Cancel
              </Button>
              <LoadingButton type="submit" isLoading={isSubmitting} className="bg-primary px-4 py-2 rounded hover:bg-primary/90">
                {editingId ? "Update" : "Create"}
              </LoadingButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent onClose={() => setDeleteId(null)}>
          <DialogHeader>
            <DialogTitle>Delete State</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 dark:text-gray-400">Are you sure you want to delete this state? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="shadow-sm">
        <CardHeader className="border-b bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <MapPin className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-2xl">States</CardTitle>
                <CardDescription className="mt-1">Manage state information</CardDescription>
              </div>
            </div>
            <Button onClick={handleOpenNew} className="shadow-sm">
              <Plus className="mr-2 h-4 w-4" />
              Add State
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Search */}
          <div className="mb-6 space-y-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Search</h3>
              {totalCount > 0 && (
                <div className="flex items-center gap-2 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                  <span>{totalCount}</span>
                  <span>Total States</span>
                </div>
              )}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search states..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10"
              />
            </div>
            {/* Active search chip */}
            {searchQuery && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-400">Active filters:</span>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-xs">
                    Search: {searchQuery}
                    <button onClick={() => setSearchQuery("")} className="hover:text-blue-900 dark:hover:text-blue-100">×</button>
                  </span>
                  <button
                    onClick={() => setSearchQuery("")}
                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 underline"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>

          <DataTable
            data={states}
            columns={columns}
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={totalPages}
            loading={isLoading}
            sortBy={sortBy}
            sortDescending={sortDescending}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onPageSizeChange={(size) => setPageSize(size)}
            onRefresh={loadStates}
            getRowKey={(s) => s.id}
            actionsRenderer={(s) => (
              <div className="flex justify-end gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleEdit(s)}
                  className="h-8 w-8 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400"
                  title="Edit State"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDeleteRequest(s.id)}
                  className="h-8 w-8 p-0 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400"
                  title="Delete State"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            emptyMessage={searchQuery ? "No states match your search" : "No states found"}
          />
        </CardContent>
      </Card>
    </div>
  );
}
